import { Field, Form, Formik } from 'formik'
import { rem } from 'polished'
import React, { useState } from 'react'
import { EMAILS } from 'shared-constants'
import styled from 'styled-components'
import { BUTTON_VARIANT, Button } from 'components/Button'
import { CheckboxGroup } from 'components/Checkbox'
import { ExpertContractContent } from 'components/ExpertContractContent'
import { Box, Flex } from 'components/Layout'
import { LinkWithText } from 'components/Link'
import { Modal } from 'components/Modal'
import { Text } from 'components/Typography'
import { ROUTE } from 'constants/routes'
import { useUserAuth } from 'providers/userAuth'
import { FONT_SIZE, MODAL_MAX_WIDTH, SPACE } from 'Theme'
import { useAcceptContractAgreementMutation } from 'types/graphql-generated'

const MODAL_TITLE = 'Designer Agreement'

const StyledCheckboxGroup = styled(CheckboxGroup)`
  label {
    width: auto;
  }
`

export const ExpertContractModal: React.FC = () => {
  const [isContractAgreementAccepted, setIsContractAgreementAccepted] =
    useState(false)
  const { refetchAndSetUserData } = useUserAuth()
  const [acceptContractAgreementMutation, { loading: isLoading }] =
    useAcceptContractAgreementMutation({
      onCompleted: () => {
        setIsContractAgreementAccepted(true)
      },
    })

  if (isContractAgreementAccepted === true) {
    return (
      <Modal
        maxWidth={MODAL_MAX_WIDTH.PX_580}
        width="100%"
        ariaLabel={MODAL_TITLE}
        title={MODAL_TITLE}
        isOpen
        onModalClose={refetchAndSetUserData}
        isFooterSticky
        customFooter={() => (
          <Flex py={SPACE.PX_14} px={SPACE.PX_30} justifyContent="flex-end">
            <Button
              width="initial"
              onClick={refetchAndSetUserData}
              variant={BUTTON_VARIANT.OUTLINE}
            >
              Close
            </Button>
          </Flex>
        )}
      >
        <Box>
          <Text as="p">
            Thank you for agreeing to the Designer Agreement. A copy of the
            agreement is now accessible through a{' '}
            <LinkWithText
              href={ROUTE.EXPERT_CONTRACT()}
              aria-label="the expert designer agreement"
              target="_blank"
            >
              direct link
            </LinkWithText>
            . You can also find this direct link anytime in your account
            settings.
          </Text>
          <Text as="p" mt={SPACE.PX_16}>
            If you have any questions, please contact us at{' '}
            <a
              href={`mailto:${EMAILS.CUSTOMER_SUPPORT}`}
              target="_blank"
              rel="noreferrer"
            >
              {EMAILS.CUSTOMER_SUPPORT}
            </a>
          </Text>
        </Box>
      </Modal>
    )
  }

  return (
    <Formik
      initialValues={{ agreementsAccepted: false }}
      onSubmit={async () => {
        await acceptContractAgreementMutation()
      }}
      enableReinitialize
    >
      {({ handleSubmit, values }) => (
        <Form>
          <Modal
            maxWidth={MODAL_MAX_WIDTH.PX_580}
            width="100%"
            maxHeight={rem(460)}
            ariaLabel={MODAL_TITLE}
            title={MODAL_TITLE}
            isOpen
            onModalClose={() => {}}
            isWithCloseIcon={false}
            isFooterSticky
            customFooter={() => (
              <Flex
                py={SPACE.PX_14}
                px={SPACE.PX_30}
                justifyContent="space-between"
              >
                <Field
                  type="checkbox"
                  name="agreementsAccepted"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {({ field: { value, ...restField }, meta }) => (
                    <StyledCheckboxGroup>
                      <CheckboxGroup.Item
                        isChecked={value}
                        isError={meta.touched && meta.error !== undefined}
                        {...restField}
                      >
                        <Text fontSize={FONT_SIZE.PX_12}>
                          I have read and agree to the terms of the Expert
                          designer agreement.
                        </Text>
                      </CheckboxGroup.Item>
                    </StyledCheckboxGroup>
                  )}
                </Field>
                <Button
                  width="initial"
                  isLoading={isLoading}
                  isDisabled={isLoading || !values.agreementsAccepted}
                  onClick={() => {
                    handleSubmit()
                  }}
                >
                  Agree
                </Button>
              </Flex>
            )}
          >
            <Box>
              <ExpertContractContent />
            </Box>
          </Modal>
        </Form>
      )}
    </Formik>
  )
}
